import React, { Component } from 'react';
import { observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { Modal } from 'shards-react';
import FeatherIcon from 'feather-icons-react';

const PAGE_GROUPS = {
  dashboard: ['/dashboard'],
  review: [
    '/reviewList', '/reviewWrite', '/reviewMassWrite', '/reviewMassList',
    '/reviewWriteConfig', '/quickReviewConfig', '/reviewWriteConfigByProduct',
    '/surveyReviewConfig', '/surveyStats', '/reviewGroup', '/snsFeed',
    '/reviewMileage', '/mileageConfig', '/replyWriteConfig', '/replyByProductList',
    '/reviewFilter', '/easySetting'
  ],
  widget: [
    '/widgetByPage', '/widgetList', '/labeling',
    '/productWidgetList', '/totalAiWidget'
  ]
};

const getPageGroup = (path) => {
  if (!path) return null;
  const norm = String(path).split('?')[0].replace(/\/$/, '') || '/';
  for (const group of Object.keys(PAGE_GROUPS)) {
    const paths = PAGE_GROUPS[group];
    if (paths.some(p => norm === p || norm.startsWith(p + '/'))) return group;
  }
  return null;
};

@inject(({ common }) => ({
  getAdminLoginNoticeList: common.getAdminLoginNoticeList
}))
@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))
@withRouter
@observer
class LoginNoticeModal extends Component {

  @observable allList = [];           // 백엔드에서 받은 활성 후보 전체
  @observable currentList = [];       // 현재 페이지 그룹에 노출 가능한 목록
  @observable currentIdx = 0;
  @observable isOpen = false;
  @observable hasFetched = false;
  isFetching = false;                 // 동시 중복 조회 방지 가드

  componentDidMount() {
    this.maybeFetchAndShow();
  }

  componentDidUpdate(prevProps) {
    const prevPath = prevProps.location && prevProps.location.pathname;
    const curPath = this.props.location && this.props.location.pathname;
    if (prevPath !== curPath) this.maybeFetchAndShow();
    // 로그인 상태 전환 시 재시도
    if (prevProps.isLogin !== this.props.isLogin && this.props.isLogin) {
      this.hasFetched = false;
      this.maybeFetchAndShow();
    }
  }

  isSkippedToday = (id) => {
    try {
      const v = localStorage.getItem('orp_loginNoticeSkipUntil_' + id);
      if (v && moment().isBefore(moment(v))) return true;
    } catch (e) {}
    try {
      const legacy = localStorage.getItem('orp_loginNoticeSkipUntil');
      if (legacy && moment().isBefore(moment(legacy))) return true;
    } catch (e) {}
    return false;
  };

  maybeFetchAndShow = async () => {
    const me = this.props.me || {};
    if (!this.props.isLogin || !me.emailId) return;
    const path = this.props.location && this.props.location.pathname;
    const group = getPageGroup(path);
    if (!group) {
      this.isOpen = false;
      return;
    }
    // 활성/비활성 토글이 즉시 반영되도록, 노출 대상 페이지 진입 시마다 최신 목록을 재조회한다.
    // (이전엔 hasFetched 로 세션당 1회만 조회 → 관리자에서 토글해도 stale 데이터로 노출되는 버그가 있었음)
    if (!this.isFetching && typeof this.props.getAdminLoginNoticeList === 'function') {
      this.isFetching = true;
      await new Promise((resolve) => {
        this.props.getAdminLoginNoticeList((list) => {
          this.allList = Array.isArray(list) ? list : [];
          this.hasFetched = true;
          this.isFetching = false;
          resolve();
        });
      });
    }
    this.applyForGroup(group);
  };

  @action
  applyForGroup = (group) => {
    const me = this.props.me || {};
    const store = me.store || {};
    const myKeys = [String(store._id || ''), String(store.platformId || ''), String(store.name || '')].filter(Boolean);
    const now = moment();
    const filtered = (this.allList || []).filter((d) => {
      if (!d || !d.isActive) return false;
      if (this.isSkippedToday(d._id)) return false;
      if (d.startAt && now.isBefore(moment(d.startAt))) return false;
      if (d.endAt && now.isAfter(moment(d.endAt))) return false;
      if (d.targetMode === 'include' || d.targetMode === 'exclude') {
        const keys = Array.isArray(d.targetStoreKeys) ? d.targetStoreKeys : [];
        const matched = keys.some((k) => myKeys.indexOf(String(k).trim()) !== -1);
        if (d.targetMode === 'include' && !matched) return false;
        if (d.targetMode === 'exclude' && matched) return false;
      }
      const pages = (Array.isArray(d.displayPages) && d.displayPages.length > 0) ? d.displayPages : ['dashboard'];
      if (pages.indexOf(group) === -1) return false;
      return true;
    });
    if (filtered.length === 0) {
      this.isOpen = false;
      this.currentList = [];
      this.currentIdx = 0;
      return;
    }
    this.currentList = filtered;
    this.currentIdx = 0;
    this.isOpen = true;
  };

  @action
  close = (skipToday) => {
    const cur = this.currentList[this.currentIdx];
    if (cur && cur._id && skipToday) {
      try { localStorage.setItem('orp_loginNoticeSkipUntil_' + cur._id, moment().endOf('day').toISOString()); } catch (e) {}
    }
    const nextIdx = this.currentIdx + 1;
    if (nextIdx < this.currentList.length) {
      this.currentIdx = nextIdx;
    } else {
      this.isOpen = false;
    }
  };

  @action
  go = (delta) => {
    const next = this.currentIdx + delta;
    if (next < 0 || next >= this.currentList.length) return;
    this.currentIdx = next;
  };

  render() {
    if (!this.isOpen || this.currentList.length === 0) return null;
    const cur = this.currentList[this.currentIdx];
    if (!cur) return null;
    const total = this.currentList.length;

    return (
      <Modal open={this.isOpen}
             toggle={() => this.close(false)}
             centered size="md"
             className="orp-login-notice-modal">
        <style>{`
          .orp-login-notice-modal .orp-ln-body h1, .orp-login-notice-modal .orp-ln-body h2, .orp-login-notice-modal .orp-ln-body h3 {
            font-weight: 700; line-height: 1.3; margin: 14px 0 8px;
          }
          .orp-login-notice-modal .orp-ln-body h1 { font-size: 22px; }
          .orp-login-notice-modal .orp-ln-body h2 { font-size: 19px; }
          .orp-login-notice-modal .orp-ln-body h3 { font-size: 16px; }
          .orp-login-notice-modal .orp-ln-body p { margin: 8px 0; font-size: 14px; line-height: 1.7; color: #334155; }
          .orp-login-notice-modal .orp-ln-body ul, .orp-login-notice-modal .orp-ln-body ol { margin: 10px 0; padding-left: 22px; font-size: 14px; line-height: 1.9; color: #334155; }
          .orp-login-notice-modal .orp-ln-body li { margin-bottom: 4px; }
          .orp-login-notice-modal .orp-ln-body blockquote {
            border-left: 3px solid #CBD5E1;
            padding: 10px 14px; margin: 12px 0;
            background: #F8FAFC; border-radius: 0 6px 6px 0;
            font-size: 13px; color: #475569;
          }
          .orp-login-notice-modal .orp-ln-body a { color: #4A6CF7; text-decoration: underline; }
          .orp-login-notice-modal .orp-ln-body img { max-width: 100%; height: auto; border-radius: 6px; margin: 8px 0; }
        `}</style>
        <div style={{background: cur.backgroundColor || '#ffffff', borderRadius: '8px', overflow: 'hidden'}}>
          <div style={{
            padding: '18px 20px',
            borderBottom: `3px solid ${cur.accentColor || '#4A6CF7'}`,
            color: cur.accentColor || '#4A6CF7',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0}}>
              <h5 style={{margin: 0, fontSize: '16px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{cur.title}</h5>
              {total > 1 && (
                <span style={{
                  padding: '2px 8px', background: 'rgba(0,0,0,0.06)',
                  color: 'inherit', borderRadius: 999, fontSize: 11, fontWeight: 700,
                  flexShrink: 0
                }}>
                  {this.currentIdx + 1} / {total}
                </span>
              )}
            </div>
            <button type="button"
                    onClick={() => this.close(false)}
                    style={{background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 4}}>
              <FeatherIcon icon="x" size="18" />
            </button>
          </div>
          <div style={{padding: '20px'}}>
            <div className="orp-ln-body"
                 dangerouslySetInnerHTML={{__html: cur.content || ''}} />
            {total > 1 && (
              <div style={{marginTop: '14px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6}}>
                <button type="button" onClick={() => this.go(-1)} disabled={this.currentIdx === 0}
                        style={{
                          border: '1px solid #E2E8F0', background: '#fff', color: '#475569',
                          width: 30, height: 30, borderRadius: '50%', cursor: this.currentIdx === 0 ? 'not-allowed' : 'pointer',
                          opacity: this.currentIdx === 0 ? 0.4 : 1,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                  <FeatherIcon icon="chevron-left" size="14" />
                </button>
                {this.currentList.map((_, i) => (
                  <span key={i} style={{
                    width: i === this.currentIdx ? 18 : 6,
                    height: 6, borderRadius: 999,
                    background: i === this.currentIdx ? (cur.accentColor || '#4A6CF7') : '#CBD5E1',
                    transition: 'all 200ms'
                  }}/>
                ))}
                <button type="button" onClick={() => this.go(1)} disabled={this.currentIdx === total - 1}
                        style={{
                          border: '1px solid #E2E8F0', background: '#fff', color: '#475569',
                          width: 30, height: 30, borderRadius: '50%', cursor: this.currentIdx === total - 1 ? 'not-allowed' : 'pointer',
                          opacity: this.currentIdx === total - 1 ? 0.4 : 1,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                  <FeatherIcon icon="chevron-right" size="14" />
                </button>
              </div>
            )}
            <div style={{marginTop: '20px', display: 'flex', justifyContent: cur.showSkipToday !== false ? 'space-between' : 'flex-end', alignItems: 'center'}}>
              {cur.showSkipToday !== false && (
                <button type="button"
                        onClick={() => this.close(true)}
                        style={{
                          background: 'none', border: 'none', color: '#64748B',
                          fontSize: '12px', cursor: 'pointer', padding: 0
                        }}>
                  오늘 하루 보지 않기
                </button>
              )}
              <button type="button"
                      onClick={() => this.close(false)}
                      style={{
                        border: 'none',
                        background: cur.accentColor || '#4A6CF7',
                        color: '#fff',
                        padding: '9px 20px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}>
                {this.currentIdx < total - 1 ? '다음 공지' : '확인'}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    );
  }
}

export default LoginNoticeModal;
